import { ApiError } from "@/common/lib/api";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { MonthSelector, currentYearMonth } from "@/features/depreciation/components/MonthSelector";
import { AlertCircle, Clock, Download, Lock, Settings2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  EVENT_TYPE_LABELS,
  type ListedJournalExport,
  type PreviewEntry,
  type SemanticKey,
  downloadExportFile,
  useCreateExport,
  useExportsList,
  usePreview,
} from "../api/journalApi";

function previousMonth(): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function periodLabel(period: string): string {
  const y = period.slice(0, 4);
  const m = Number(period.slice(5, 7));
  const months = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];
  return `${months[m - 1]} ${y}`;
}

// Convierte "YYYY-MM" en (YYYY-MM-01, YYYY-MM-DD-último-del-mes).
function periodToRange(period: string): { start: string; end: string } {
  const y = Number(period.slice(0, 4));
  const m = Number(period.slice(5, 7));
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    start: `${period}-01`,
    end: `${period}-${String(lastDay).padStart(2, "0")}`,
  };
}

export function ReportesPage() {
  const { currentAccount, currentMembership } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";
  const role = currentMembership?.role ?? null;
  const canSeal = role === "owner" || role === "admin";
  const [period, setPeriod] = useState<string>(previousMonth);

  const previewQuery = usePreview(accountId, period);
  const exportsQuery = useExportsList(accountId);

  return (
    <>
      <title>Reportes contables · HATO</title>
      <header className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-medium text-3xl text-app-ink-1 tracking-tight">Reportes contables</h1>
          <p className="mt-1 text-app-ink-3 text-sm">
            Preview de los asientos contables del período. Una vez sellado, los asientos quedan
            inmutables.
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:items-end">
          <MonthSelector value={period} onChange={setPeriod} />
          <Link
            to="/app/ajustes/mapeo-cuentas"
            className="inline-flex items-center gap-1.5 self-start rounded-xl border border-app-border bg-app-surface px-3 py-1.5 font-medium text-[13px] text-app-ink-2 hover:bg-app-surface-2 sm:self-end"
          >
            <Settings2 className="h-3.5 w-3.5" aria-hidden />
            Configurar mapeo de cuentas
          </Link>
        </div>
      </header>

      {previewQuery.isLoading && <PreviewSkeleton />}

      {previewQuery.isError && (
        <div className="mt-12 flex flex-col items-center gap-4 text-center">
          <p className="text-app-ink-2 text-sm">No se pudo generar el preview.</p>
          <button
            type="button"
            onClick={() => void previewQuery.refetch()}
            className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
          >
            Reintentar
          </button>
        </div>
      )}

      {previewQuery.data?.kind === "missing" && (
        <MissingMappingsBanner missing={previewQuery.data.missing} />
      )}

      {previewQuery.data?.kind === "ready" && (
        <PreviewContent
          accountId={accountId}
          period={period}
          entries={previewQuery.data.data.entries}
          totals={previewQuery.data.data.totals}
          canSeal={canSeal}
          exports={exportsQuery.data?.exports ?? []}
        />
      )}

      <ExportsHistorySection
        accountId={accountId}
        canSeal={canSeal}
        loading={exportsQuery.isLoading}
        data={exportsQuery.data?.exports ?? []}
      />
    </>
  );
}

function MissingMappingsBanner({ missing }: { missing: SemanticKey[] }) {
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" aria-hidden />
        <div className="min-w-0">
          <h2 className="font-medium text-amber-900">Faltan cuentas del sistema</h2>
          <p className="mt-1 text-amber-800 text-sm">
            Para generar el preview de este período necesitás que existan las siguientes cuentas en
            tu plan de cuentas:
          </p>
          <ul className="mt-2 list-disc space-y-0.5 pl-5 text-amber-900 text-sm">
            {missing.length === 0 ? (
              <li>(no se especificaron — revisá el plan de cuentas)</li>
            ) : (
              missing.map((s) => <li key={s}>{s}</li>)
            )}
          </ul>
          <Link
            to="/app/ajustes/plan-de-cuentas"
            className="mt-3 inline-block rounded-xl bg-amber-700 px-3 py-1.5 font-medium text-[13px] text-white hover:bg-amber-800"
          >
            Ir al plan de cuentas
          </Link>
        </div>
      </div>
    </div>
  );
}

function PreviewContent({
  accountId,
  period,
  entries,
  totals,
  canSeal,
  exports,
}: {
  accountId: string;
  period: string;
  entries: PreviewEntry[];
  totals: { debits: string; credits: string };
  canSeal: boolean;
  exports: ListedJournalExport[];
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const createExport = useCreateExport(accountId);
  const hasEntries = entries.length > 0;

  const { start, end } = periodToRange(period);
  // B89: el botón distingue 3 estados. La data de sellados ya está cargada en la
  // página (useExportsList) — no se agrega endpoint nuevo.
  const sealedExport = exports.find((e) => e.periodStart === start && e.periodEnd === end) ?? null;
  const isFuture = period >= currentYearMonth(); // mes en curso o posterior

  const onConfirmSeal = async () => {
    try {
      const result = await createExport.mutateAsync({
        periodStart: start,
        periodEnd: end,
      });
      setConfirmOpen(false);
      toast.success(`Export sellado · SHA-256 ${result.contentHash.slice(0, 8)}…`, {
        description:
          result.adjustmentCount > 0
            ? `${result.eventCount} eventos + ${result.adjustmentCount} ajustes`
            : `${result.eventCount} eventos`,
      });
      const filename = `journal-${start}_${end}.xlsx`;
      try {
        await downloadExportFile(accountId, result.id, filename);
      } catch {
        toast.error("El export quedó sellado, pero la descarga falló. Probá desde el listado.");
      }
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 422 && e.code === "MAPPINGS_INCOMPLETE") {
          toast.error("Faltan mappings contables. Configurá antes de sellar.");
        } else if (e.status === 400 && e.code === "FUTURE_PERIOD") {
          toast.error("El período no puede estar en el futuro.");
        } else if (e.status === 400 && e.code === "INVALID_PERIOD") {
          toast.error("Rango de fechas inválido.");
        } else {
          toast.error(`No se pudo sellar el período (${e.code}).`);
        }
      } else {
        toast.error("No se pudo sellar el período.");
      }
    }
  };

  if (!hasEntries) {
    return (
      <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-app-border border-dashed bg-app-surface px-6 py-12 text-center">
        <p className="font-medium text-app-ink-1">No hay asientos para {periodLabel(period)}</p>
        <p className="max-w-sm text-app-ink-3 text-sm">
          Cuando registres compras, costos a lotes, transiciones, depreciación o muertes en este
          período, aparecerán acá como asientos sugeridos para tu contador.
        </p>
        {/* Edge: un período sellado cuyo preview vivo quedó vacío igual debe
            poder re-descargarse (la mayoría de sellados sí tienen entries). */}
        {canSeal && sealedExport && (
          <SealButton
            accountId={accountId}
            start={start}
            end={end}
            hasEntries={false}
            isFuture={isFuture}
            sealedExport={sealedExport}
            isPending={createExport.isPending}
            onSeal={() => setConfirmOpen(true)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-app-ink-3 text-sm">
          {entries.length} {entries.length === 1 ? "asiento" : "asientos"} en {periodLabel(period)}
        </p>
        {canSeal ? (
          <SealButton
            accountId={accountId}
            start={start}
            end={end}
            hasEntries={hasEntries}
            isFuture={isFuture}
            sealedExport={sealedExport}
            isPending={createExport.isPending}
            onSeal={() => setConfirmOpen(true)}
          />
        ) : (
          <span
            className="inline-flex items-center gap-2 self-start rounded-2xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-3"
            title="Solo dueños y admins pueden sellar exports"
          >
            <Lock className="h-4 w-4" aria-hidden />
            Solo lectura (rol viewer)
          </span>
        )}
      </div>

      <ul className="space-y-3">
        {entries.map((e) => (
          <EntryCard key={e.eventId} entry={e} />
        ))}
      </ul>

      <Totals totals={totals} />

      {confirmOpen && (
        <ConfirmSealDialog
          period={period}
          isPending={createExport.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => void onConfirmSeal()}
        />
      )}
    </div>
  );
}

// B89: el control de sellado/descarga con sus 3 estados (solo rama canSeal).
function SealButton({
  accountId,
  start,
  end,
  hasEntries,
  isFuture,
  sealedExport,
  isPending,
  onSeal,
}: {
  accountId: string;
  start: string;
  end: string;
  hasEntries: boolean;
  isFuture: boolean;
  sealedExport: ListedJournalExport | null;
  isPending: boolean;
  onSeal: () => void;
}) {
  const [downloading, setDownloading] = useState(false);

  // Estado 1 — ya sellado: descarga directa (re-descarga idempotente, mismos
  // bytes). NO abre el confirm de sellado.
  if (sealedExport) {
    const sealedOn = new Date(sealedExport.exportedAt).toLocaleDateString("es-CR", {
      day: "numeric",
      month: "numeric",
    });
    const onDownload = async () => {
      setDownloading(true);
      try {
        await downloadExportFile(accountId, sealedExport.id, `journal-${start}_${end}.xlsx`);
      } catch {
        toast.error("No se pudo descargar el archivo.");
      } finally {
        setDownloading(false);
      }
    };
    return (
      <div className="flex flex-col items-start gap-1 sm:items-end">
        <button
          type="button"
          onClick={() => void onDownload()}
          disabled={downloading}
          className="inline-flex items-center gap-2 self-start rounded-2xl border border-app-border bg-app-surface-2 px-4 py-2 font-medium text-[13px] text-app-ink-1 enabled:hover:bg-app-surface disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Download className="h-4 w-4" aria-hidden />
          {downloading ? "Descargando…" : "Descargar Excel"}
        </button>
        <span className="text-app-ink-3 text-xs">Sellado el {sealedOn}</span>
      </div>
    );
  }

  // Estado 2 — mes en curso o posterior, sin sellar: aún no se puede sellar.
  if (isFuture) {
    return (
      <button
        type="button"
        disabled
        title="El período no terminó: no se puede sellar todavía"
        className="inline-flex cursor-not-allowed items-center gap-2 self-start rounded-2xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-3 opacity-70"
      >
        <Clock className="h-4 w-4" aria-hidden />
        El mes aún no terminó
      </button>
    );
  }

  // Estado 3 — sellable: comportamiento actual (abre el confirm).
  return (
    <button
      type="button"
      onClick={onSeal}
      disabled={!hasEntries || isPending}
      className="inline-flex items-center gap-2 self-start rounded-2xl border border-app-border bg-app-surface-2 px-4 py-2 font-medium text-[13px] text-app-ink-1 enabled:hover:bg-app-surface disabled:cursor-not-allowed disabled:opacity-60"
    >
      <Download className="h-4 w-4" aria-hidden />
      Sellar y descargar Excel
    </button>
  );
}

function ConfirmSealDialog({
  period,
  isPending,
  onCancel,
  onConfirm,
}: {
  period: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      // biome-ignore lint/a11y/useSemanticElements: shadcn-style modal usa div con role=dialog; HTMLDialogElement no soporta backdrop click cerrar trivial
      role="dialog"
      aria-modal="true"
      aria-labelledby="seal-dialog-title"
    >
      <div className="w-full max-w-md rounded-2xl border border-app-border bg-app-surface p-5 shadow-lg">
        <h2 id="seal-dialog-title" className="font-medium text-app-ink-1 text-lg">
          Sellar {periodLabel(period)}
        </h2>
        <p className="mt-2 text-app-ink-2 text-sm">
          Una vez sellado, los asientos del período quedan inmutables. Correcciones futuras
          generarán asientos de ajuste en el mes corriente.
        </p>
        <p className="mt-3 text-app-ink-2 text-sm">
          Al sellar el período, se generan automáticamente los asientos de cierre del mes y, si es
          diciembre, los del año fiscal. El reporte mayorizado se incluye como una hoja del Excel.
        </p>
        <p className="mt-3 text-app-ink-3 text-xs">
          El archivo se descarga automáticamente al finalizar.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-app-ink-1 px-4 py-2 font-medium text-[13px] text-app-bg disabled:opacity-50"
          >
            {isPending ? "Sellando…" : "Sellar y descargar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExportsHistorySection({
  accountId,
  canSeal,
  loading,
  data,
}: {
  accountId: string;
  canSeal: boolean;
  loading: boolean;
  data: ListedJournalExport[];
}) {
  if (loading) return null;
  if (data.length === 0) return null;
  return (
    <section className="mt-10">
      <h2 className="mb-3 font-medium text-app-ink-1 text-lg">Exports anteriores</h2>
      <p className="mb-4 text-app-ink-3 text-sm">
        Re-descargar es idempotente: el archivo regenerado tiene los mismos bytes que el original.
      </p>
      <ul className="space-y-2">
        {data.map((e) => (
          <ExportHistoryRow key={e.id} accountId={accountId} item={e} canSeal={canSeal} />
        ))}
      </ul>
    </section>
  );
}

function ExportHistoryRow({
  accountId,
  item,
  canSeal,
}: {
  accountId: string;
  item: ListedJournalExport;
  canSeal: boolean;
}) {
  void canSeal; // todos los roles permitidos pueden re-descargar
  const [downloading, setDownloading] = useState(false);
  const exportedAt = new Date(item.exportedAt).toLocaleDateString("es-CR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const onDownload = async () => {
    setDownloading(true);
    try {
      const filename = `journal-${item.periodStart}_${item.periodEnd}.xlsx`;
      await downloadExportFile(accountId, item.id, filename);
    } catch {
      toast.error("No se pudo descargar el archivo.");
    } finally {
      setDownloading(false);
    }
  };
  return (
    <li className="flex flex-col gap-2 rounded-2xl border border-app-border bg-app-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="font-medium text-app-ink-1 text-sm">
          {item.periodStart} → {item.periodEnd}
        </div>
        <div className="mt-0.5 text-app-ink-3 text-xs">
          {item.eventCount} eventos
          {item.adjustmentCount > 0 ? ` + ${item.adjustmentCount} ajustes` : ""} · sellado{" "}
          {exportedAt}
          {item.exportedByName ? ` por ${item.exportedByName}` : ""}
        </div>
        <div className="font-mono text-[10px] text-app-ink-3">SHA-256 {item.contentHash}</div>
      </div>
      <button
        type="button"
        onClick={() => void onDownload()}
        disabled={downloading}
        className="inline-flex items-center gap-1.5 self-start rounded-xl border border-app-border bg-app-surface-2 px-3 py-1.5 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface disabled:opacity-50"
      >
        <Download className="h-3.5 w-3.5" aria-hidden />
        {downloading ? "Descargando…" : "Descargar"}
      </button>
    </li>
  );
}

function EntryCard({ entry }: { entry: PreviewEntry }) {
  const date = new Date(entry.occurredAt).toLocaleDateString("es-CR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return (
    <li className="rounded-2xl border border-app-border bg-app-surface p-4">
      <header className="mb-3 flex flex-col gap-1 border-app-border border-b pb-2 sm:flex-row sm:items-baseline sm:justify-between">
        <div>
          <span className="font-medium text-app-ink-1 text-sm">
            {EVENT_TYPE_LABELS[entry.eventType] ?? entry.eventType}
          </span>
          <span className="ml-2 text-app-ink-3 text-xs">{date}</span>
        </div>
        <span className="text-app-ink-3 text-xs">{entry.memo}</span>
      </header>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-app-ink-3 text-xs">
            <th className="pb-1 text-left font-normal">Cuenta</th>
            <th className="pb-1 text-right font-normal">Debe</th>
            <th className="pb-1 text-right font-normal">Haber</th>
          </tr>
        </thead>
        <tbody>
          {entry.lines.map((l, i) => (
            <tr
              // biome-ignore lint/suspicious/noArrayIndexKey: lines are positional, no stable id
              key={i}
              className="border-app-border border-t"
            >
              <td className="py-2 text-app-ink-1">
                <div className="font-mono text-xs">{l.account.code}</div>
                <div className="text-[11px] text-app-ink-3">{l.account.name}</div>
              </td>
              <td className="py-2 text-right tabular-nums">{l.debit !== "0.00" ? l.debit : ""}</td>
              <td className="py-2 text-right tabular-nums">
                {l.credit !== "0.00" ? l.credit : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </li>
  );
}

function Totals({ totals }: { totals: { debits: string; credits: string } }) {
  const balanced = totals.debits === totals.credits;
  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-app-border bg-app-surface-2 p-4 sm:flex-row sm:items-center sm:justify-between">
      <span
        className={
          balanced ? "font-medium text-app-ink-1 text-sm" : "font-medium text-red-700 text-sm"
        }
      >
        {balanced ? "Asientos cuadrados" : "Asientos NO cuadran (revisar)"}
      </span>
      <div className="flex gap-6 text-sm tabular-nums">
        <div>
          <span className="mr-2 text-app-ink-3 text-xs">Σ Debe</span>
          <span className="font-medium text-app-ink-1">₡{totals.debits}</span>
        </div>
        <div>
          <span className="mr-2 text-app-ink-3 text-xs">Σ Haber</span>
          <span className="font-medium text-app-ink-1">₡{totals.credits}</span>
        </div>
      </div>
    </div>
  );
}

function PreviewSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
          key={i}
          className="h-32 animate-pulse rounded-2xl border border-app-border bg-app-surface-2"
        />
      ))}
    </div>
  );
}
